/**
 * SEO HEADING STRUCTURE - Study With Me Live
 * ===========================================
 * 
 * TITLE: Study With Me Live - Global Study Community | CSW
 * META DESCRIPTION: Study with me live sessions with global community, 24/7 support, Pomodoro timer...
 * 
 * H1: "Study With Me Live: Building Your Global Study Community"
 * 
 * H2 tags:
 *   - "Building a Global Study Community"
 *   - "Continuous Support and Availability"
 *   - "Enhanced Study Experience"
 *   - "Motivation and Accountability"
 *   - "Frequently Asked Questions"
 *   - "Join the Community"
 */

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  Clock, ListTodo, Music, Focus, ArrowRight, Play, Users, Brain, 
  Headphones, BookOpen, HelpCircle, Sparkles, Heart, Globe, 
  MessageCircle, Target, Trophy, BarChart3, Shield, Zap, Coffee
} from "lucide-react";
import { Link } from "react-router-dom";

const faqData = [
  {
    question: "Why should I study with others online?",
    answer: "Being part of a supportive peer group increases motivation, satisfaction and efficiency, while reducing anxiety. Collaborative environments also enhance transversal skills like creativity and communication."
  },
  {
    question: "Isn't online learning lonely?",
    answer: "Not on our platform! Research on online peer support underscores that these communities empower participants by reducing isolation and providing emotional support. With features like silent study calls and chat rooms, you can connect whenever you need support."
  },
  {
    question: "Do timers really help?",
    answer: "Yes—pre-planned breaks like the Pomodoro technique produce better moods and more efficient sessions than self-regulated breaks. Our timers ensure you maximize your focus without burning out."
  },
  {
    question: "How can I stay motivated?",
    answer: "Setting clear goals improves academic outcomes, and gamification elements like leaderboards and badges enhance engagement. Our platform combines both: you set session goals, track your progress and earn rewards along the way."
  },
  {
    question: "Is there support for mental health?",
    answer: "While our platform is not a replacement for professional help, research shows that online peer support networks can reduce social isolation and empower users. Our community offers a safe space to share challenges and successes, and our moderators maintain a supportive environment."
  },
  {
    question: "What makes this different from YouTube Study With Me videos?",
    answer: "Unlike passive videos, our platform offers interactive features: real-time peer connections, personal timers, goal tracking, community leaderboards, and chat support. You're not just watching—you're participating in a global study community."
  }
];

const StudyWithMe = () => {
  // JSON-LD Schema - Comprehensive WebPage
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Study With Me Live - Global Study Community | CSW",
    "description": "Study with me live sessions with global community, 24/7 support, Pomodoro timer, lo-fi music, and evidence-based study techniques. Join thousands of students worldwide.",
    "url": "https://csw.live/study-with-me/",
    "inLanguage": "en",
    "dateModified": new Date().toISOString().split('T')[0],
    "isPartOf": {
      "@type": "WebSite",
      "name": "CSW - Online Workspace",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW",
      "url": "https://csw.live"
    },
    "about": [
      { "@type": "Thing", "name": "Study With Me" },
      { "@type": "Thing", "name": "Virtual Study Room" },
      { "@type": "Thing", "name": "Body Doubling" },
      { "@type": "Thing", "name": "Pomodoro Technique" },
      { "@type": "Thing", "name": "Online Study Community" },
      { "@type": "Thing", "name": "Peer Learning" },
      { "@type": "Thing", "name": "Collaborative Learning" }
    ],
    "mentions": [
      { "@type": "Thing", "name": "Study With Me Live" },
      { "@type": "Thing", "name": "Global Study Community" },
      { "@type": "Thing", "name": "Virtual Coworking" },
      { "@type": "Thing", "name": "Study Accountability" },
      { "@type": "Thing", "name": "Lo-Fi Study Music" },
      { "@type": "Thing", "name": "Pomodoro Timer" },
      { "@type": "Thing", "name": "Flowtime Technique" },
      { "@type": "Thing", "name": "Task Management" },
      { "@type": "Thing", "name": "Study Stream" },
      { "@type": "Thing", "name": "Online Workspace" },
      { "@type": "Thing", "name": "Body Doubling ADHD" },
      { "@type": "Thing", "name": "Social Facilitation" },
      { "@type": "Thing", "name": "Gamification" },
      { "@type": "Thing", "name": "24/7 Study Room" },
      { "@type": "Thing", "name": "Study Motivation" },
      { "@type": "Thing", "name": "Peer Support" },
      { "@type": "Thing", "name": "Self-Efficacy" },
      { "@type": "Thing", "name": "Learning Analytics" },
      { "@type": "Thing", "name": "Ambient Noise Study" },
      { "@type": "Thing", "name": "Focusmate Alternative" },
      { "@type": "Thing", "name": "StudyTogether Alternative" }
    ],
    "keywords": "study with me, study with me live, global study community, virtual study room, body doubling, pomodoro timer, flowtime technique, lo-fi study music, peer support, study accountability, collaborative learning, gamification, 24/7 study room, free study room, study motivation, self-efficacy, learning analytics, ambient noise study, online peer support, study together online, focusmate alternative"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CSW Study With Me Live",
    "alternateName": ["Study With Me", "CSW Global Study Community"],
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "ratingCount": "3000",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "Global Study Community",
      "24/7 Virtual Study Rooms",
      "Pomodoro & Flowtime Timers",
      "Community Leaderboards",
      "Goal Setting & Tracking",
      "Atmospheric Backgrounds",
      "Lo-Fi Focus Music",
      "Chat with Tutors",
      "Silent Study Calls",
      "Progress Analytics",
      "Badges & Rewards",
      "Peer Accountability",
      "Mental Health Support"
    ],
    "description": "CSW Study With Me Live offers a global study community with 24/7 virtual rooms, evidence-based timers, peer support, gamification, and learning analytics. Join thousands of students worldwide."
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://csw.live/" },
      { "@type": "ListItem", "position": 2, "name": "Study Stream", "item": "https://csw.live/study-stream/" },
      { "@type": "ListItem", "position": 3, "name": "Study With Me Live", "item": "https://csw.live/study-with-me/" }
    ]
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        title="Study With Me Live - Global Study Community"
        description="Study with me live sessions with global community, 24/7 support, Pomodoro timer, lo-fi music, and evidence-based study techniques. Join thousands of students worldwide."
        keywords="study with me, study with me live, global study community, virtual study room, body doubling, pomodoro timer, peer support, 24/7 study room, collaborative learning, gamification"
        canonicalUrl="/study-with-me/"
      />
      
      {/* JSON-LD Schemas */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <Globe className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">Global Study Community • 24/7 Available</span>
                </div>
                
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                  <span className="text-gradient">Study With Me Live:</span>
                  <br />
                  <span className="text-foreground">Building Your Global Study Community</span>
                </h1>
                
                <p className="text-xl text-muted-foreground font-light max-w-xl">
                  Welcome to Study With Me Live, where students from around the world come together to focus, learn and thrive. Our platform isn't just another productivity timer—it's a full ecosystem designed to help you reach your academic goals through meaningful connections and evidence-based features.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button 
                    size="lg" 
                    className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Join the Community
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                    asChild
                  >
                    <Link to="/study-stream/">
                      <Play className="mr-2 h-5 w-5" />
                      Explore Study Stream
                    </Link>
                  </Button>
                </div>
                
                <div className="flex items-center gap-8 pt-8">
                  <div>
                    <p className="text-3xl font-bold text-gradient">24/7</p>
                    <p className="text-sm text-muted-foreground">Always Open</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Global</p>
                    <p className="text-sm text-muted-foreground">Community</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Free</p>
                    <p className="text-sm text-muted-foreground">Forever</p>
                  </div>
                </div>
              </div>
              
              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                  <video 
                    src="/videos/productivity-demo.mp4" 
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
                
                <div className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-md border border-border rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Evidence-Based Platform</p>
                      <p className="text-xl font-bold text-foreground">Focus, Learn & Thrive</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <Users className="w-6 h-6 text-primary-foreground" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 1: Building a Global Study Community */}
        <section id="global-community" className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
          <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border mb-6">
                <Globe className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Research-Backed</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Building a{" "}
                <span className="text-gradient">Global Study Community</span>
              </h2>
            </div>
            
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Connect Across Continents */}
              <Card className="bg-card/50 border-border/50 hover:border-primary/50 transition-all p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-display font-bold mb-4">Connect Across Continents</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Isolation is one of the biggest obstacles to effective self-directed learning. A{" "}
                  <a href="https://doi.org/10.3389/fpsyg.2025.1582857" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    recent study of more than 500 university students
                  </a>{" "}
                  using "Study With Me" found that real-time peer support significantly increased intrinsic and extrinsic motivation, self-efficacy and learning satisfaction while reducing test anxiety.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  Our virtual study rooms and community leaderboards make it easy to find study pals, chat with others and see your progress relative to a global student community.
                </p>
              </Card>
              
              {/* Cultural Exchange */}
              <Card className="bg-card/50 border-border/50 hover:border-primary/50 transition-all p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Sparkles className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-display font-bold mb-4">Cultural Exchange & Cooperation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Collaborative learning isn't just fun; it develops valuable skills.{" "}
                  <a href="https://link.springer.com/article/10.1186/s40561-024-00361-2" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    University-level interventions
                  </a>{" "}
                  show that integrating collaboration and gamification improves transversal competencies such as creativity, problem solving and communication.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  By sharing the same virtual space with peers from different backgrounds, you pick up diverse study techniques and perspectives. Our community leaderboard tracks contributions in a supportive way.
                </p>
              </Card>
              
              {/* Data-Driven Benefits */}
              <Card className="bg-card/50 border-border/50 hover:border-primary/50 transition-all p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Heart className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-display font-bold mb-4">Data-Driven Community Benefits</h3>
                <p className="text-muted-foreground leading-relaxed">
                  In a collaborative, gamified online environment,{" "}
                  <a href="https://link.springer.com/article/10.1007/s10639-023-11879-2" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    positive social interactions correlate with better emotional experiences
                  </a>{" "}
                  and reduce stress. Digital resources like ours can boost emotional well-being, allowing students to cope better with academic pressures.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  Our atmospheric backgrounds and study stream rooms replicate the feel of a communal library or café, giving you the comfort of a physical study environment.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Section 2: Continuous Support and Availability */}
        <section id="support" className="py-24 px-4 relative">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]" />
          
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border mb-6">
                <Clock className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Always Available</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Continuous{" "}
                <span className="text-gradient">Support & Availability</span>
              </h2>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div className="space-y-8">
                {/* Around-the-clock access */}
                <div className="p-6 rounded-2xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-display font-bold mb-3">Around-the-Clock Access</h3>
                      <p className="text-muted-foreground leading-relaxed">
                        Learning doesn't always fit within a 9-to-5 schedule, and time zones vary.{" "}
                        <a href="https://doi.org/10.3389/feduc.2021.638470" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          Research on online education
                        </a>{" "}
                        emphasized that digital platforms provide 24/7 access, allowing students to ask questions and receive feedback whenever they study. That's why our study rooms are always open.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Global Peer Support */}
                <div className="p-6 rounded-2xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-display font-bold mb-3">Global Peer Support</h3>
                      <p className="text-muted-foreground leading-relaxed">
                        <a href="https://mental.jmir.org/2023/1/e41855" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          Systematic reviews on online peer-support networks
                        </a>{" "}
                        show they remove geographical barriers, offer emotional and informational support at any time and can empower users by increasing self-efficacy and reducing social isolation.
                      </p>
                      <p className="text-muted-foreground leading-relaxed mt-3">
                        Our chat with tutors and community help channels let you ask questions, share resources or simply vent to someone who understands.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-8">
                {/* Structured Yet Flexible */}
                <div className="p-6 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-display font-bold mb-3">Structured Yet Flexible</h3>
                      <p className="text-muted-foreground leading-relaxed">
                        Flexibility doesn't mean a lack of structure. A{" "}
                        <a href="https://doi.org/10.1111/bjep.12593" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          2023 experiment comparing break-taking strategies
                        </a>{" "}
                        found that students who used pre-determined "systematic" breaks (such as 25-minute Pomodoro blocks with 6-minute breaks) experienced better mood and efficiency compared with those who self-regulated their breaks.
                      </p>
                      <p className="text-muted-foreground leading-relaxed mt-3">
                        Our platform offers timers based on popular methods like the{" "}
                        <Link to="/pomodoro-technique/" className="text-primary hover:underline">Pomodoro technique</Link>{" "}
                        and Flowtime. Choose a schedule that works for you.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Stats Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-card/50 border-border/50 p-6 text-center">
                    <Clock className="w-8 h-8 text-primary mx-auto mb-3" />
                    <p className="text-3xl font-bold text-gradient mb-1">24/7</p>
                    <p className="text-sm text-muted-foreground">Always Open</p>
                  </Card>
                  <Card className="bg-card/50 border-border/50 p-6 text-center">
                    <Globe className="w-8 h-8 text-primary mx-auto mb-3" />
                    <p className="text-3xl font-bold text-gradient mb-1">100+</p>
                    <p className="text-sm text-muted-foreground">Countries</p>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Enhanced Study Experience */}
        <section id="experience" className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/50 to-background" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border mb-6">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Evidence-Based</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Enhanced{" "}
                <span className="text-gradient">Study Experience</span>
              </h2>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Left Column */}
              <div className="space-y-8">
                {/* Evidence-Based Techniques */}
                <Card className="bg-card/50 border-border/50 p-8">
                  <Brain className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-display font-bold mb-4">Evidence-Based Techniques</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Modern study platforms go beyond simple timers. A{" "}
                    <a href="https://doi.org/10.3390/bs15070861" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      comparison of self-regulated, Pomodoro and Flowtime techniques
                    </a>{" "}
                    showed that regulated breaks help manage fatigue and motivation by reducing the mental load of deciding when to stop.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    Our <Link to="/study-timer/" className="text-primary hover:underline">personal timers</Link> and session goals eliminate guesswork so you can focus on learning rather than on scheduling your breaks.
                  </p>
                </Card>
                
                {/* Productivity Boosters */}
                <Card className="bg-card/50 border-border/50 p-8">
                  <Headphones className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-display font-bold mb-4">Productivity Boosters</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Moderate background sound can actually boost creativity and problem-solving:{" "}
                    <a href="https://www.sciencedaily.com/releases/2012/05/120514134332.htm" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      researchers found that ambient noise around 70 dB
                    </a>{" "}
                    promotes abstract thinking, whereas too little or too much noise hinders performance.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    That's why our atmospheric backgrounds and curated focus music mimic the gentle hum of a café or library. You can adjust the volume to create a personalized soundscape.
                  </p>
                </Card>
              </div>
              
              {/* Right Column */}
              <div className="space-y-8">
                {/* Integrated Tools */}
                <Card className="bg-card/50 border-border/50 p-8">
                  <ListTodo className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-display font-bold mb-4">Integrated Tools</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Our platform combines goal planning, <Link to="/task-management/" className="text-primary hover:underline">task management</Link>, progress tracking and focus music into one seamless workflow.{" "}
                    <a href="https://doi.org/10.18608/jla.2016.32.8" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Education researchers argue
                    </a>{" "}
                    that focusing solely on grades undermines motivation and that tracking effort—through visual analytics and frequent feedback—helps students reflect on their learning process.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    By monitoring your tasks, study stats and mood, you'll gain insight into what works for you.
                  </p>
                </Card>
                
                {/* Peer Interaction */}
                <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 p-8">
                  <Trophy className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-display font-bold mb-4">Peer Interaction for Better Outcomes</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    <a href="https://doi.org/10.1007/s10639-022-10983-z" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Gamified features like leaderboards and badges
                    </a>{" "}
                    not only make studying more engaging but also foster cooperation and help develop soft skills. Students in gamified, collaborative environments report improved transversal competences and higher academic performance.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    Our community leaderboard and reward system add a touch of friendly competition without becoming high stakes; you earn points and badges for consistent effort.
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Motivation and Accountability */}
        <section id="motivation" className="py-24 px-4 relative">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]" />
          
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border mb-6">
                <Target className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Goal-Driven</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Motivation &{" "}
                <span className="text-gradient">Accountability</span>
              </h2>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Goal Setting */}
              <div className="p-8 rounded-2xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all">
                <Target className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-display font-bold mb-4">Goal Setting & Self-Efficacy</h3>
                <p className="text-muted-foreground leading-relaxed">
                  A{" "}
                  <a href="https://doi.org/10.3389/fpsyg.2024.1324007" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    2024 study demonstrated
                  </a>{" "}
                  that self-efficacy predicts the goals students set for themselves, which in turn mediate learning outcomes. Our goal-setting feature encourages you to set realistic session goals and then track your progress.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  Another{" "}
                  <a href="https://www.ncssfl.org/wp-content/uploads/2017/11/MLJ.2012.GoalSettingandStudentAchievementLongitudinalStudy.pdf" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    five-year longitudinal study across 23 high schools
                  </a>{" "}
                  found a statistically significant relationship between goal setting and achievement; mastery-oriented goals foster deeper engagement.
                </p>
              </div>
              
              {/* Gamification */}
              <div className="p-8 rounded-2xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all">
                <Trophy className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-display font-bold mb-4">Gamification & Rewards</h3>
                <p className="text-muted-foreground leading-relaxed">
                  <a href="https://doi.org/10.30476/JAMP.2023.98953.1817" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Gamification can improve learning outcomes, attitudes and motivation.
                  </a>{" "}
                  A quasi-experimental study using narrative, avatar, levels and points found that gamified interventions improved students' attitudes and correlated learning with feedback and challenge.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  Our system's progress tracking, reward tiers and leaderboards leverage these elements to turn studying into a series of attainable quests. When you achieve milestones, you receive badges that reinforce your sense of progress.
                </p>
              </div>
              
              {/* Peer Accountability */}
              <div className="p-8 rounded-2xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all">
                <Users className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-display font-bold mb-4">Peer Accountability</h3>
                <p className="text-muted-foreground leading-relaxed">
                  "Study With Me" doesn't just connect you with peers; it keeps you accountable.{" "}
                  <a href="https://doi.org/10.3389/fpsyg.2025.1582857" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Research on peer support
                  </a>{" "}
                  shows that interactions with study buddies can extend study time, improve efficiency and build discipline.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  Our study stats feature lets you and your peers see how much time you've spent focusing, and our study flow (focus-break-reflection) encourages you to reflect at the end of each session.
                </p>
              </div>
              
              {/* Continuous Improvement */}
              <div className="p-8 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
                <BarChart3 className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-display font-bold mb-4">Continuous Improvement</h3>
                <p className="text-muted-foreground leading-relaxed">
                  <a href="https://doi.org/10.18608/jla.2016.32.8" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Data from learning analytics
                  </a>{" "}
                  highlight the importance of measuring and visualizing effort rather than focusing solely on grades. By reviewing your personal dashboard, you can see patterns in your study sessions, identify when you're most productive and adjust your strategies.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  Our leaderboards emphasize participation and consistency rather than high test scores, fostering a growth mindset.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Study Mode Links */}
        <section className="py-16 px-4 relative">
          <div className="max-w-6xl mx-auto">
            <div className="bg-card/50 border border-border rounded-2xl p-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-display font-bold mb-2">Explore Other Study Modes</h3>
                <p className="text-muted-foreground">Not ready for solo? Try these collaborative options:</p>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link to="/study-with-friends/" className="p-4 rounded-xl bg-background/50 border border-border hover:border-primary/50 transition-all text-center group">
                  <Users className="w-6 h-6 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <p className="font-medium">Study With Friends</p>
                  <p className="text-xs text-muted-foreground">Private rooms</p>
                </Link>
                <Link to="/study-with-strangers/" className="p-4 rounded-xl bg-background/50 border border-border hover:border-primary/50 transition-all text-center group">
                  <Users className="w-6 h-6 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <p className="font-medium">Study With Strangers</p>
                  <p className="text-xs text-muted-foreground">Meet new people</p>
                </Link>
                <Link to="/group-study/" className="p-4 rounded-xl bg-background/50 border border-border hover:border-primary/50 transition-all text-center group">
                  <Users className="w-6 h-6 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <p className="font-medium">Group Study</p>
                  <p className="text-xs text-muted-foreground">Team sessions</p>
                </Link>
                <Link to="/zoom-study-room/" className="p-4 rounded-xl bg-background/50 border border-border hover:border-primary/50 transition-all text-center group">
                  <Play className="w-6 h-6 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <p className="font-medium">Zoom Study Room</p>
                  <p className="text-xs text-muted-foreground">No time limits</p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-24 px-4 relative overflow-hidden">
          <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="max-w-3xl mx-auto relative z-10">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border mb-6">
                <HelpCircle className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Common Questions</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Frequently Asked{" "}
                <span className="text-gradient">Questions</span>
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-card/50 border border-border/50 rounded-xl px-6 data-[state=open]:border-primary/50"
                >
                  <AccordionTrigger className="text-left font-display font-semibold hover:no-underline py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Join the Community CTA */}
        <section id="join" className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
              Join the{" "}
              <span className="text-gradient">Community</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-6 max-w-3xl mx-auto">
              Thousands of students worldwide have discovered the benefits of studying together. With Study With Me Live you'll gain 24/7 access to collaborative study spaces, personalized timers, goal-tracking tools and a welcoming global community.
            </p>
            <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
              Whether you're preparing for exams, learning a new language or simply looking to build better study habits, our evidence-based platform is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start Your Journey Today
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-border hover:bg-card font-semibold px-10 py-7 text-lg"
                asChild
              >
                <Link to="/blog/science-behind-study-with-me/">
                  Read the Research
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StudyWithMe;
